<template>
  <div class="event-ranking-page">
    <div class="event-bar">
      <div class="wrap inner">
        <img
          v-if="auction.cover"
          class="event-thumb"
          :src="auction.cover"
          :alt="auction.title"
        />
        <div class="event-meta">
          <h2>{{ auction.title }}</h2>
          <div class="sub">
            <span v-if="location" class="pill">{{ location }}</span>
            <span v-if="providerLabel">{{ providerLabel }}</span>
          </div>
        </div>
        <a v-if="auction.url" class="breadcrumb" :href="auction.url">
          <span>Evento</span>
          <span>›</span>
          <span class="now">Ranking genético</span>
        </a>
      </div>
    </div>

    <main class="wrap">
      <section class="hero">
        <span class="eyebrow reveal">Ranking genético exclusivo</span>
        <h1 class="reveal">
          Os melhores animais deste evento por <em>iABCZ</em>
        </h1>
        <p class="lead reveal">
          Selecionamos o mérito genético de cada lote não vendido deste leilão.
          O pódio reúne os animais de maior índice — a elite absoluta do evento.
        </p>
        <p class="metric-note reveal">
          somente lotes disponíveis deste evento
        </p>
      </section>

      <template v-if="!isEmpty">
        <section class="podium">
          <a
            v-for="animal in podiumDisplay"
            :key="animal.position"
            class="pcard reveal"
            :class="podiumRankClass(animal.position)"
            :href="animal.link"
            @click="trackCardClick"
          >
            <div class="rankbadge tnum">{{ animal.position }}</div>
            <img class="avatar" :src="animal.thumb" :alt="animal.name" />
            <div class="name">{{ animal.name }}</div>
            <div v-if="animal.provider_name" class="farm">
              {{ animal.provider_name }}
            </div>
            <div class="lote">Lote {{ animal.batch_number }}</div>
            <div class="score">
              <div class="val tnum">{{ formatScore(animal.zebu_index) }}</div>
              <div class="lbl">iABCZ</div>
            </div>
            <span v-if="animal.deca" class="deca">
              {{ animal.deca }}
            </span>
            <span class="cta">Ver lote →</span>
          </a>
        </section>

        <section v-if="topTier.length" class="sec">
          <div class="sec-head">
            <h3>Top 10 do evento</h3>
            <span class="count">a elite genética deste leilão</span>
            <span class="rule"></span>
          </div>
          <div class="tier">
            <div class="tier-grid">
              <a
                v-for="(animal, i) in topTier"
                :key="animal.position || `sold-${i}`"
                class="elite"
                :class="{ sold: animal.sold }"
                :href="animal.link"
                @click="trackCardClick"
              >
                <span
                  v-if="animal.sold"
                  class="rk sold-mark"
                  title="Animal vendido"
                  aria-label="Animal vendido"
                >
                  <GavelIcon />
                </span>
                <span v-else class="rk tnum">{{ animal.position }}</span>
                <img
                  class="av"
                  :src="animal.thumb"
                  :alt="animal.name"
                  loading="lazy"
                />
                <span class="info">
                  <span class="nm">
                    {{ animal.name }}
                    <span v-if="animal.sold" class="sold-tag">Vendido</span>
                  </span>
                  <span v-if="animal.provider_name" class="fm">
                    {{ animal.provider_name }}
                  </span>
                </span>
                <span class="sc">
                  <b class="tnum">{{ formatScore(animal.zebu_index) }}</b>
                  <span>iABCZ</span>
                </span>
              </a>
            </div>
          </div>
        </section>

        <section v-if="fullList.length" class="sec">
          <div class="sec-head">
            <h3>Ranking completo</h3>
            <span class="count">todos os {{ ranking.length }} animais disponíveis no evento</span>
            <span class="rule"></span>
          </div>
          <div class="list-head">
            <span>#</span>
            <span>Animal</span>
            <span>Fornecedor</span>
            <span>iABCZ</span>
            <span></span>
          </div>
          <div class="list">
            <a
              v-for="animal in fullList"
              :key="animal.position"
              class="row"
              :href="animal.link"
              @click="trackCardClick"
            >
              <span class="rk tnum">{{ animal.position }}</span>
              <span class="animal">
                <img
                  class="av"
                  :src="animal.thumb"
                  :alt="animal.name"
                  loading="lazy"
                />
                <span>
                  <span class="nm">{{ animal.name }}</span>
                  <span class="br">{{ breedAndLote(animal) }}</span>
                </span>
              </span>
              <span class="farm">{{ animal.provider_name }}</span>
              <span class="iabcz tnum">
                {{ formatScore(animal.zebu_index) }}<small>iABCZ</small>
              </span>
              <span class="go">›</span>
            </a>
          </div>
        </section>
      </template>

      <section v-else class="sec">
        <div class="empty">
          <div class="em-ic">🐂</div>
          <h3>Ainda não há ranking genético para este evento</h3>
          <p>
            Os lotes deste leilão ainda não têm índice iABCZ cadastrado, ou
            todos já foram vendidos. Assim que houver dados genéticos, os
            melhores animais aparecem aqui.
          </p>
          <a v-if="auction.url" class="btn-primary back" :href="auction.url">
            Voltar ao evento
          </a>
        </div>
      </section>

      <div v-if="auction.url" class="backcta">
        <a class="btn-primary" :href="auction.url">
          Voltar ao evento
        </a>
      </div>
    </main>
  </div>
</template>

<script>
import { computed, onMounted } from "vue";
import GavelIcon from "icons/cached/gavel.svg";

// Tier labels come pre-computed from the backend payload (TASK_01):
// 'podium' (pos <= 3), 'top' (pos <= 10), 'list' (rest). We slice on this
// field and never recompute sizes here — no magic numbers.
const TIER = { PODIUM: "podium", TOP: "top", LIST: "list" };

const VISIT_EVENT = "Event - Ranking Visited";
const CLICK_EVENT = "Event - Ranking Card Clicked";

const track = (eventName, data) => {
  if (typeof window.mixpanel === "undefined") return;
  window.mixpanel.track(eventName, data);
};

export default {
  components: {
    GavelIcon,
  },
  props: {
    ranking: { type: Array, default: () => [] },
    fillers: { type: Array, default: () => [] },
    auction: { type: Object, default: () => ({}) },
    mixpanelData: { type: Object, default: () => ({}) },
  },
  setup(props) {
    const isEmpty = computed(() => props.ranking.length === 0);

    const podium = computed(() =>
      props.ranking.filter((a) => a.tier === TIER.PODIUM),
    );
    // Top 10 repeats the podium (positions 1-3) together with positions 4-10.
    // When the event has fewer than 10 unsold animals, the backend appends the
    // best-iABCZ SOLD animals as `fillers` to complete the row — these have no
    // position and render a purchase icon instead of a rank number.
    const topTier = computed(() => {
      const ranked = props.ranking
        .filter((a) => a.tier === TIER.PODIUM || a.tier === TIER.TOP)
        .sort((a, b) => a.position - b.position);
      return [...ranked, ...props.fillers];
    });
    // Full ranking lists every animal of the event, from 1st to last.
    const fullList = computed(() =>
      [...props.ranking].sort((a, b) => a.position - b.position),
    );

    // Desktop podium order is [2nd, 1st, 3rd] so the champion sits centered and
    // raised (mobile reorders the champion first via CSS). Missing positions
    // (fewer than 3 ranked animals) are dropped gracefully.
    const podiumDisplay = computed(() => {
      const byPosition = {};
      podium.value.forEach((a) => {
        byPosition[a.position] = a;
      });
      return [byPosition[2], byPosition[1], byPosition[3]]
        .filter(Boolean)
        .map((a) => ({ ...a, deca: formatDeca(a.zebu_ranking) }));
    });

    const podiumRankClass = (position) => {
      if (position === 1) return "first";
      if (position === 2) return "second";
      return "third";
    };

    const formatScore = (value) => {
      if (value === null || value === undefined) return "—";
      return value.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    // zebu_ranking arrives in inconsistent shapes from ABCZ ("DECA 1", "1",
    // "deca 3", "DECA1"…). Normalize to the canonical "DECA <n>" display.
    const formatDeca = (value) => {
      if (value === null || value === undefined) return null;
      const match = String(value).match(/\d+/);
      return match ? `DECA ${match[0]}` : null;
    };

    const breedAndLote = (animal) =>
      [animal.breed, `Lote ${animal.batch_number}`].filter(Boolean).join(" · ");

    const trackCardClick = () => track(CLICK_EVENT, props.mixpanelData);

    onMounted(() => {
      track(VISIT_EVENT, props.mixpanelData);
    });

    return {
      isEmpty,
      podiumDisplay,
      topTier,
      fullList,
      podiumRankClass,
      formatScore,
      breedAndLote,
      trackCardClick,
    };
  },
  computed: {
    location() {
      const parts = [this.auction.city, this.auction.state].filter(Boolean);
      return parts.join(" - ");
    },
    providerLabel() {
      const providers = this.auction.providers || [];
      if (providers.length === 0) return null;
      if (providers.length > 1) return "Diversos fornecedores";
      return providers[0];
    },
  },
};
</script>
